/*
  FVスライドショーの再生制御

  1. オープニング演出
     画面中央にロゴだけを表示し、1枚目の画像の読み込みが終わってからロゴをフェードアウトする。
     フェードアウトが終わってから自動再生を開始する。
     「同じ訪問中は1回だけ」の判定は、マークアップ直後のインラインスクリプトが担当する
     （描画される前に消す必要があるため）。このファイルは初回表示時の進行だけを扱う。

  2. 次のスライドの画像が読み込めるまで待つ
     枚数が多いと、読み込みが終わっていないスライドに切り替わって点滅したように見えるため。

  3. 自動再生が止まったままにならないようにする
     Flickity には次の挙動があり、放っておくと自動再生が戻らない。
     - スライドをクリック/タップ/ドラッグすると stopPlayer() で完全停止する
     - 完全停止すると visibilitychange の監視も外れるので、タブに戻っても再開しない
     - ブラウザの戻る/進む（bfcache）で復帰するとタイマーだけが失われ、内部状態は
       「再生中」のまま残るため、playPlayer() を呼んでも早期リターンして何も起きない
     そこで「止まっていそうな場面」では stop → play で強制的に組み直す。
*/
(function () {
  'use strict';

  var SESSION_KEY = 'mf:slideshow-intro';

  /* 回線が遅いときに待ち続けないための上限 */
  var MAX_IMAGE_WAIT = 6000;

  /* スライダーの初期化を待つ上限（50ms × 60 = 3秒） */
  var SLIDER_READY_TRIES = 60;

  /* 操作で止まったあと、再開するまでの待ち時間 */
  var RESUME_AFTER_INTERACTION = 2000;

  /* 画像の読み込み待ちで止めている間は、強制再開をしない */
  var waitingForImage = false;

  /* 読み込みの先回り（1枚目・2枚目の準備）中は、強制再開をしない */
  var priming = false;

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* プライベートモードなどで保存できない場合は記録しない */
    }
  }

  function slidesOf(slider) {
    return Array.prototype.slice.call(slider.querySelectorAll('.slideshow__slide'));
  }

  function introVisible() {
    return document.querySelector('[data-slideshow-intro]') !== null;
  }

  /* 指定したスライドの中の画像が読み込み終わるのを待つ（上限つき） */
  function whenImageReady(slide, timeout, callback) {
    var img = slide ? slide.querySelector('img') : null;

    if (!img || img.complete) {
      callback();
      return;
    }

    var done = false;

    function finish() {
      if (done) return;
      done = true;
      callback();
    }

    img.addEventListener('load', finish, { once: true });
    img.addEventListener('error', finish, { once: true });
    window.setTimeout(finish, timeout);
  }

  /* slider-component が Flickity を作り終わるのを待つ */
  function whenSliderReady(slider, callback) {
    if (slider.flkty) {
      callback(slider.flkty);
      return;
    }

    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;

      if (slider.flkty) {
        window.clearInterval(timer);
        callback(slider.flkty);
      } else if (tries > SLIDER_READY_TRIES) {
        window.clearInterval(timer);
        callback(null);
      }
    }, 50);
  }

  /* 次のスライドの画像が読み込めるまで自動再生を止めて待つ */
  function holdUntilNextSlideReady(slider, flkty) {
    if (!flkty || typeof flkty.on !== 'function') return;

    flkty.on('change', function (index) {
      var slides = slidesOf(slider);
      if (slides.length < 2) return;

      var next = slides[(index + 1) % slides.length];
      var img = next ? next.querySelector('img') : null;
      if (!img || img.complete) return;

      waitingForImage = true;
      flkty.pausePlayer();

      whenImageReady(next, MAX_IMAGE_WAIT, function () {
        waitingForImage = false;
        flkty.unpausePlayer();
      });
    });
  }

  /* 自動再生を組み直す。state を見ずに stop → play するのが要点 */
  function keepPlaying(slider, flkty) {
    if (!slider.sliderOptions || !slider.sliderOptions.autoPlay) return;

    var restartTimer = null;

    function restart(delay) {
      window.clearTimeout(restartTimer);

      restartTimer = window.setTimeout(function () {
        if (document.hidden) return;

        /* オープニング中・画像の読み込み待ち中は、そちらの制御に任せる */
        if (introVisible() || waitingForImage || priming) return;

        var player = slider.flkty;
        if (!player || !player.player) return;

        player.stopPlayer();
        player.playPlayer();
      }, delay || 0);
    }

    /* 操作で完全停止したあと、少し待ってから再開する */
    if (flkty && typeof flkty.on === 'function') {
      flkty.on('uiChange', function () {
        restart(RESUME_AFTER_INTERACTION);
      });
      flkty.on('pointerDown', function () {
        restart(RESUME_AFTER_INTERACTION);
      });
    }

    /* タブに戻ってきたとき */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) restart(0);
    });

    /* ブラウザの戻る/進むで復帰したとき、ウインドウにフォーカスが戻ったとき */
    window.addEventListener('pageshow', function () {
      restart(0);
    });
    window.addEventListener('focus', function () {
      restart(0);
    });
  }

  /*
    自動再生を開始してよい状態になるまで待つ。オープニングの有無にかかわらず毎回通る。
    - 1枚目の画像が読めるまで待つ（オープニングがある場合はその間ロゴを出したまま）
    - さらに2枚目の画像が読めるまで待ってから自動再生を開始する
      （1回目の切り替え先が未読込だと、そこで点滅して見えるため）
    2回目以降の訪問では画像がブラウザのキャッシュにあるので、ここはほぼ待たずに通過する。
  */
  function startWhenReady(slider, root) {
    var slides = slidesOf(slider);
    var hasIntro = root !== null && root.parentNode !== null;
    var hold = hasIntro ? parseInt(root.getAttribute('data-hold'), 10) || 0 : 0;
    var fade = hasIntro ? parseInt(root.getAttribute('data-fade'), 10) || 0 : 0;
    var startedAt = Date.now();

    function stopPlayer() {
      if (slider.flkty) slider.flkty.stopPlayer();
    }

    priming = true;

    /* 初期化のタイミングが前後しても確実に止まるよう、開始するまで繰り返し止める */
    var holdTimer = window.setInterval(stopPlayer, 100);
    stopPlayer();

    function begin() {
      window.clearInterval(holdTimer);
      priming = false;

      /* テーマ編集画面で自動再生をOFFにしている場合は、このイベントでは何も起きない */
      slider.dispatchEvent(new CustomEvent('theme:slider:deselect'));
    }

    whenImageReady(slides[0], MAX_IMAGE_WAIT, function () {
      /* 1枚目が出せる状態になったので、FVをフワッと表示する */
      var reveal = document.querySelector('.slideshow--reveal');
      if (reveal) reveal.classList.add('is-revealed');

      /* オープニングの最低表示時間を満たすまで待つ */
      var remaining = Math.max(0, hold - (Date.now() - startedAt));

      window.setTimeout(function () {
        if (hasIntro) root.classList.add('is-hiding');

        window.setTimeout(function () {
          if (hasIntro && root.parentNode) {
            root.parentNode.removeChild(root);
            sessionSet(SESSION_KEY, '1');
          }

          whenImageReady(slides[1], MAX_IMAGE_WAIT, begin);
        }, fade);
      }, remaining);
    });
  }

  function run() {
    var slider = document.querySelector('[data-slider][data-slider-fullwidth]');
    var root = document.querySelector('[data-slideshow-intro]');

    if (!slider) {
      if (root && root.parentNode) root.parentNode.removeChild(root);
      return;
    }

    var target = root ? document.getElementById(root.getAttribute('data-slider-target')) : null;
    if (target) slider = target;

    whenSliderReady(slider, function (flkty) {
      holdUntilNextSlideReady(slider, flkty);
      keepPlaying(slider, flkty);
      startWhenReady(slider, root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
